/**
 * QuickSwitcherCommand · v2.7.0 batch DO+
 * =======================================
 * Global Cmd/Ctrl+K floating widget that opens a command palette listing the
 * available modes the current user can jump to. Reuses the same backend
 * bridges as ModeSwitcher (so the destination tokens are real, not mocked).
 *
 * Mount once at the root of each surface; pass `mode` so we know what the
 * caller is currently in. Hides itself silently when no destination is
 * available (e.g. an unlinked rider).
 */
import { useEffect, useState, useCallback } from "react";
import { ArrowLeftRight, Car, Shield, User, Code2, Loader2 } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

import adminApi from "../lib/adminApi";
import api from "../lib/api";
import customerApi from "../lib/customerApi";

const LAST_MODE_KEY = "rtg_last_mode";

async function getRoles(mode) {
  try {
    if (mode === "admin") return (await adminApi.get("/admin/me/roles")).data;
    if (mode === "rider") return (await customerApi.get("/customer/me/roles")).data;
    if (mode === "driver") {
      const u = JSON.parse(localStorage.getItem("rtg_user") || "{}");
      const rs = Array.from(new Set([u.role, ...(u.roles || [])])).filter(Boolean);
      return { roles: rs };
    }
  } catch { /* no-op */ }
  return { roles: [] };
}

export function QuickSwitcherCommand({ mode }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [can, setCan] = useState({});

  // Compute available modes once (and when reopened) — same logic as
  // ModeSwitcher kept here on purpose so the two stay decoupled.
  useEffect(() => {
    let active = true;
    (async () => {
      const data = await getRoles(mode);
      if (!active) return;
      const roles = data.roles || [];
      const isAdmin = roles.some((r) =>
        ["operating_partner", "developer", "dispatcher", "team_leader", "field_supervisor", "supervisor", "admin"].includes(r)
      );
      const isDriver = roles.includes("driver") || isAdmin;
      setCan({
        admin: mode !== "admin" && isAdmin,
        driver: mode !== "driver" && isDriver,
        rider: mode !== "rider",
        developer: roles.includes("developer"),
      });
    })();
    return () => { active = false; };
  }, [mode]);

  // Cmd/Ctrl+K binding
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const jump = useCallback(async (target) => {
    setBusy(true);
    try {
      if (mode === "admin" && target === "rider") {
        const { data } = await adminApi.post("/admin/staff/rider-mode");
        localStorage.setItem("rtg_customer_token", data.access_token);
        localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
        localStorage.setItem(LAST_MODE_KEY, "rider");
        window.location.href = "/ride";
      } else if (mode === "admin" && target === "driver") {
        const { data } = await adminApi.post("/admin/staff/driver-mode");
        localStorage.setItem("rtg_token", data.access_token);
        localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
        localStorage.setItem("rtg_user", JSON.stringify(data.driver));
        localStorage.setItem(LAST_MODE_KEY, "driver");
        window.location.href = "/dashboard";
      } else if (mode === "driver" && target === "admin") {
        const { data } = await api.post("/driver/staff/dispatch-mode");
        localStorage.setItem("rtg_admin_token", data.access_token);
        localStorage.setItem("rtg_admin", JSON.stringify(data.admin));
        localStorage.setItem(LAST_MODE_KEY, "admin");
        window.location.href = "/admin";
      } else if (mode === "driver" && target === "rider") {
        // v2.8 · Direct mint so ANY driver can ride as themselves.
        const { data: r } = await api.post("/driver/rider-mode");
        localStorage.setItem("rtg_customer_token", r.access_token);
        localStorage.setItem("rtg_customer", JSON.stringify(r.customer));
        localStorage.setItem(LAST_MODE_KEY, "rider");
        window.location.href = "/ride";
      } else if (mode === "rider" && (target === "admin" || target === "driver")) {
        toast.message("Sign in with your dispatch account to continue");
        window.location.href = target === "admin" ? "/admin/login" : "/login";
      } else if (target === "developer") {
        window.location.href = "/admin#sub=developer-portal";
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not switch to ${target}`);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }, [mode]);

  const noOptions = !can.admin && !can.driver && !can.rider && !can.developer;
  if (noOptions) return null;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <VisuallyHidden asChild>
        <DialogTitle>Quick mode switcher</DialogTitle>
      </VisuallyHidden>
      <VisuallyHidden asChild>
        <DialogDescription>Press Cmd+K to switch between Admin, Driver, Rider, and Developer modes.</DialogDescription>
      </VisuallyHidden>
      <CommandInput
        placeholder="Switch mode... (Cmd+K)"
        data-testid="quick-switcher-input"
      />
      <CommandList data-testid="quick-switcher-list">
        <CommandEmpty>No matches.</CommandEmpty>
        <CommandGroup heading="Modes">
          {can.admin && (
            <CommandItem onSelect={() => jump("admin")} data-testid="quick-switch-admin" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Shield className="h-4 w-4 mr-2 text-amber-400"/>}
              <span>Admin Console</span>
              <kbd className="ml-auto text-[10px] text-zinc-500">/admin</kbd>
            </CommandItem>
          )}
          {can.driver && (
            <CommandItem onSelect={() => jump("driver")} data-testid="quick-switch-driver" disabled={busy}>
              <Car className="h-4 w-4 mr-2 text-amber-400"/>
              <span>Driver App</span>
              <kbd className="ml-auto text-[10px] text-zinc-500">/dashboard</kbd>
            </CommandItem>
          )}
          {can.rider && (
            <CommandItem onSelect={() => jump("rider")} data-testid="quick-switch-rider" disabled={busy}>
              <User className="h-4 w-4 mr-2 text-blue-300"/>
              <span>Rider Portal</span>
              <kbd className="ml-auto text-[10px] text-zinc-500">/ride</kbd>
            </CommandItem>
          )}
          {can.developer && (
            <CommandItem onSelect={() => jump("developer")} data-testid="quick-switch-developer" disabled={busy}>
              <Code2 className="h-4 w-4 mr-2 text-emerald-300"/>
              <span>Developer Area</span>
              <kbd className="ml-auto text-[10px] text-zinc-500">dev</kbd>
            </CommandItem>
          )}
        </CommandGroup>
        <CommandGroup heading="Tip">
          <CommandItem disabled className="opacity-60 text-[11px]">
            <ArrowLeftRight className="h-3 w-3 mr-2"/>
            Press <kbd className="px-1 mx-1 rounded bg-zinc-800">Cmd+K</kbd> / <kbd className="px-1 mx-1 rounded bg-zinc-800">Ctrl+K</kbd> anywhere
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export default QuickSwitcherCommand;
